import { useCallback, useState } from 'react';
import { 
  TextInput, 
  toast,
  Select,
  DataTable
} from '@etherisc/ui-kit';
import { PositiveButton, NeutralButton } from '../../components/buttons';
import { ShapefileUploadDialog } from '../../components/shapefile';
import { useShapefiles } from '../../hooks/useShapefiles';
import { shapefileService } from '../../services/climate/spatial/ShapefileService';
import type { Shapefile } from '../../types/shapefile';
import type { ActionDefinition } from '../../utils/tableHelpers';
import {
  UploadIcon,
  DownloadIcon,
  TrashIcon,
  EyeIcon,
  SearchIcon,
  FilterIcon,
} from 'lucide-react';

// Column helpers import
import { 
  textColumn,
  dateColumn,
  statusColumn,
  numberColumn,
  actionsColumn
} from '../../utils/columns';

interface ShapefileManagementProps {
  onViewDetails?: (shapefile: Shapefile) => void;
}

export function ShapefileManagement({ onViewDetails }: ShapefileManagementProps) {
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });
  const showError = (message: string, description?: string) => toast.error(message, { description });
  
  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  const {
    shapefiles,
    filteredShapefiles,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    pagination,
    totalShapefiles,
    activeShapefiles,
    inactiveShapefiles,
    refreshShapefiles,
    fetchShapefiles,
    deleteShapefileOptimistically,
  } = useShapefiles();

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    fetchShapefiles(1, filters, pagination.limit);
  }, [filters, pagination.limit, fetchShapefiles, setSearchQuery]);

  // Handle geometry type filter
  const handleGeometryTypeFilter = useCallback((geometryType: string) => {
    const newFilters = {
      ...filters,
      geometryType: geometryType === 'all' ? undefined : geometryType,
    };
    setFilters(newFilters);
    fetchShapefiles(1, newFilters, pagination.limit);
  }, [filters, setFilters, fetchShapefiles, pagination.limit]);

  // Handle active status filter
  const handleActiveStatusFilter = useCallback((status: string) => {
    const newFilters = {
      ...filters,
      isActive: status === 'all' ? undefined : status === 'active',
    };
    setFilters(newFilters);
    fetchShapefiles(1, newFilters, pagination.limit);
  }, [filters, setFilters, fetchShapefiles, pagination.limit]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    setSearchQuery('');
    fetchShapefiles(1, emptyFilters, pagination.limit);
  }, [setFilters, setSearchQuery, fetchShapefiles, pagination.limit]);

  // Handle shapefile actions (simplified for now)
  const handleUpload = useCallback(() => {
    setUploadDialogOpen(true);
  }, []);

  const handleView = useCallback((shapefile: Shapefile) => {
    if (onViewDetails) {
      onViewDetails(shapefile);
    } else {
      showSuccess('View Details', `Viewing details for "${shapefile.name}"`);
    }
  }, [onViewDetails, showSuccess]);

  const handleDownload = useCallback(async (shapefile: Shapefile) => {
    try {
      const blob = await shapefileService.downloadShapefile(shapefile.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${shapefile.filename}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('Download Started', `Downloading "${shapefile.name}"`);
    } catch (error) {
      console.error('Error downloading shapefile:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to download shapefile.';
      showError('Download Failed', errorMessage);
    }
  }, [showSuccess, showError]);

  const handleDelete = useCallback(async (shapefile: Shapefile) => {
    if (!confirm(`Are you sure you want to delete shapefile "${shapefile.name}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      // Optimistically update the UI first
      deleteShapefileOptimistically(shapefile.id);
      showSuccess('Shapefile Deleted', `Shapefile "${shapefile.name}" has been deleted successfully.`);

      // Then make the API call
      await shapefileService.deleteShapefile(shapefile.id);
    } catch (error) {
      console.error('Error deleting shapefile:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete shapefile.';
      showError('Deletion Failed', errorMessage);
      
      // Revert the optimistic update on error by refreshing data
      refreshShapefiles();
    }
  }, [deleteShapefileOptimistically, showSuccess, showError, refreshShapefiles]);

  // Define action definitions for the table
  const actionDefinitions: ActionDefinition<Shapefile>[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: <EyeIcon size={16} />,
      onClick: handleView,
      intent: 'outline',
    },
    {
      id: 'download',
      label: 'Download Shapefile',
      icon: <DownloadIcon size={16} />,
      onClick: handleDownload,
      intent: 'outline',
    },
    {
      id: 'delete',
      label: 'Delete Shapefile',
      icon: <TrashIcon size={16} />,
      onClick: handleDelete,
      intent: 'danger',
      disabled: (shapefile) => !shapefile.isActive,
    },
  ];

  // Column definitions using standardized patterns
  const columns = [
    // Name using textColumn pattern
    textColumn<Shapefile>('name', {
      header: 'Name',
      maxLength: 30
    }),
    
    // Filename using textColumn pattern  
    textColumn<Shapefile>('filename', {
      header: 'Filename',
      emptyDisplay: '-',
      maxLength: 25,
      className: 'font-mono text-sm'
    }),
    
    // Geometry Type using statusColumn for better visual representation
    statusColumn<Shapefile>('geometryType', {
      header: 'Type',
      customVariants: {
        'Polygon': 'info',
        'MultiPolygon': 'info',
        'Point': 'success',
        'LineString': 'warning'
      },
      defaultVariant: 'neutral',
      getValue: (shapefile) => {
        const sf = shapefile as Shapefile;
        return sf.geometryType || 'Unknown';
      }
    }),
    
    // Feature Count using numberColumn pattern
    numberColumn<Shapefile>('featureCount', {
      header: 'Features',
      precision: 0,
      emptyDisplay: '0'
    }),
    
    // File Size using textColumn pattern with custom formatting (bytes converted to MB)
    textColumn<Shapefile>('fileSize', {
      header: 'Size',
      emptyDisplay: 'N/A',
      className: 'text-right font-mono text-sm',
      getValue: (shapefile) => {
        const sf = shapefile as Shapefile;
        if (!sf.fileSize || sf.fileSize === 0) return 'N/A';
        const sizeInMB = sf.fileSize / (1024 * 1024);
        return `${sizeInMB.toFixed(1)} MB`;
      }
    }),
    
    // Area using numberColumn pattern for proper numeric formatting
    numberColumn<Shapefile>('totalAreaKm2', {
      header: 'Area (km²)',
      precision: 2,
      showThousandsSeparator: true,
      emptyDisplay: 'N/A'
    }),
    
    // Coverage using textColumn pattern with custom getValue
    textColumn<Shapefile>('coverage', {
      header: 'Coverage',
      emptyDisplay: 'No data',
      getValue: (shapefile) => {
        const sf = shapefile as Shapefile;
        if (!sf.coverage) return 'No data';
        return `${sf.coverage.completeness.toFixed(1)}% (${sf.coverage.dataPoints} pts)`;
      }
    }),
    
    // Status using statusColumn to show validation status with appropriate variants
    statusColumn<Shapefile>('validationStatus', {
      header: 'Status',
      customVariants: {
        'valid': 'success',
        'invalid': 'error',
        'pending': 'warning',
        'processing': 'pending'
      },
      customLabels: {
        'valid': 'Valid',
        'invalid': 'Invalid', 
        'pending': 'Pending',
        'processing': 'Processing'
      },
      defaultVariant: 'neutral',
      transformLabel: (label) => label.charAt(0).toUpperCase() + label.slice(1)
    }),
    
    // Upload date using dateColumn pattern
    dateColumn<Shapefile>('uploadedAt', {
      header: 'Uploaded',
      format: 'relative'
    }),
    
    // Actions using actionsColumn pattern
    actionsColumn<Shapefile>(actionDefinitions, {
      size: 'md'
    }),
  ];

  // Filter options
  const geometryTypes = Array.from(new Set(shapefiles.map(s => s.geometryType).filter(Boolean)));
  const geometryTypeOptions = [
    { value: 'all', label: 'All Types' },
    ...geometryTypes.map(type => ({ value: type, label: type })),
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shapefile Management</h1>
          <p className="text-gray-600 mt-1">
            Manage geographical shapefiles for climate data coverage and analysis
          </p>
        </div>
        <PositiveButton onClick={handleUpload}>
          <UploadIcon className="w-4 h-4" />
          Upload Shapefile
        </PositiveButton>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary">{totalShapefiles}</div>
          <div className="text-sm text-base-content/70">Total Shapefiles</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-success">{activeShapefiles}</div>
          <div className="text-sm text-base-content/70">Active Shapefiles</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-error">{inactiveShapefiles}</div>
          <div className="text-sm text-base-content/70">Inactive Shapefiles</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <FilterIcon className="w-5 h-5" />
            Search and Filters
          </h3>
          {(searchQuery || filters.geometryType || filters.isActive !== undefined) && (
            <NeutralButton onClick={handleClearFilters} className="text-sm">
              Clear All
            </NeutralButton>
          )}
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-base-content/50 z-10" />
          <div className="pl-10">
            <TextInput
              placeholder="Search shapefiles by name, filename, or type..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Filter Row */}
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Geometry Type"
            options={geometryTypeOptions}
            value={filters.geometryType || 'all'}
            onValueChange={(value: string) => handleGeometryTypeFilter(value)}
            placeholder="All Types"
          />
          <Select
            label="Status"
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active Only' },
              { value: 'inactive', label: 'Inactive Only' },
            ]}
            value={filters.isActive === undefined ? 'all' : filters.isActive ? 'active' : 'inactive'}
            onValueChange={(value: string) => handleActiveStatusFilter(value)}
            placeholder="All Status"
          />
        </div>
        
        {/* Active Filters Display */}
        {(searchQuery || filters.geometryType || filters.isActive !== undefined) && (
          <div className="text-sm text-gray-600">
            Active filters: {[
              searchQuery && `Search: "${searchQuery}"`,
              filters.geometryType && `Type: ${filters.geometryType}`,
              filters.isActive !== undefined && `Status: ${filters.isActive ? 'Active' : 'Inactive'}`
            ].filter(Boolean).join(', ')}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Shapefiles</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <NeutralButton onClick={refreshShapefiles}>
                  Try Again
                </NeutralButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shapefiles Table */}
      <DataTable
        data={filteredShapefiles}
        columns={columns}
        enableSorting={true}
        enableResizing={true}
        loading={loading}
        manualPagination={true}
        pageCount={pagination.totalPages}
        rowCount={pagination.total}
        onPaginationChange={(paginationState) => {
          const newPage = paginationState.pageIndex + 1; // Convert 0-based to 1-based
          const newPageSize = paginationState.pageSize;
          
          // Let the DataTable handle pagination state, just fetch data
          fetchShapefiles(newPage, filters, newPageSize);
        }}
        state={{
          pagination: {
            pageIndex: pagination.page - 1, // Convert 1-based to 0-based
            pageSize: pagination.limit,
          },
        }}
        pagination={{
          showSizeSelector: true,
          showPageInfo: true,
          showNavigation: true,
          enableFastNavigation: true,
          enableJumpToPage: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
      />

      {/* Upload Dialog */}
      <ShapefileUploadDialog
        isOpen={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={(shapefile: Shapefile) => {
          setUploadDialogOpen(false);
          refreshShapefiles();
          showSuccess('Upload Successful', `Shapefile "${shapefile.name}" uploaded successfully.`);
        }}
        onError={(error: string) => {
          showError('Upload Failed', error);
        }}
      />
    </div>
  );
} 